import { AxiosError } from "axios";
import { createApi } from "../../../../utils/createApi";

/**
 * Bulk email alert recipients (ENG-2467).
 *
 * A camera's email alert recipients live on `streams[0].ses_recipients` as a
 * comma-separated string, edited one camera at a time on Edit Camera. Sites
 * onboarded by duplicating one camera then need the recipients set on every
 * copy, so camera admin exposes one site-scoped bulk endpoint:
 *
 *   POST cameras/bulk_ses_recipients/
 *   { site_id, camera_ids? (omit = all), mode, emails, dry_run }
 *
 * `dry_run: true` is the preview: same response, nothing saved. Multi-stream
 * cameras change on every stream; before/after come from streams[0], and a
 * camera with no streams reports changed: false. Changed cameras are marked
 * undeployed server-side, like the per-camera save. 404 = unknown site.
 */

export type BulkEmailMode = "add" | "remove" | "replace";

export const BULK_EMAIL_MODES: BulkEmailMode[] = ["add", "remove", "replace"];

export const BULK_EMAIL_MODE_LABELS: Record<BulkEmailMode, string> = {
  add: "Add",
  remove: "Remove",
  replace: "Replace",
};

export const BULK_EMAIL_MODE_DESCRIPTIONS: Record<BulkEmailMode, string> = {
  add: "Add these emails to each camera, keeping its current recipients.",
  remove: "Remove these emails from each camera; other recipients stay.",
  replace: "Replace each camera's recipients with exactly these emails.",
};

export interface BulkEmailCameraResult {
  camera_id: number;
  camera_name: string;
  before: string[];
  after: string[];
  changed: boolean;
}

export interface BulkEmailResponse {
  site_id: number;
  mode: BulkEmailMode;
  dry_run: boolean;
  changed_count: number;
  results: BulkEmailCameraResult[];
}

export interface BulkEmailRequest {
  siteId: number;
  cameraIds?: number[];
  mode: BulkEmailMode;
  emails: string[];
  dryRun: boolean;
}

// Deliberately simple: one @, no spaces, a dot in the domain. The backend is
// the authority (it answers `invalid_emails`); this only catches typos early.
const EMAIL_PATTERN = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

export const isValidEmail = (email: string): boolean =>
  EMAIL_PATTERN.test(email.trim());

/**
 * Split pasted or typed text into email tokens. Commas, semicolons, any
 * whitespace and newlines all separate; empties are dropped and duplicates
 * (case-insensitive) collapse to the first spelling.
 */
export const parseEmailInput = (text: string): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  text
    .split(/[\s,;]+/)
    .map((token) => token.trim())
    .filter(Boolean)
    .forEach((token) => {
      const key = token.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        out.push(token);
      }
    });
  return out;
};

/** Merge new tokens into an existing chip list without duplicating. */
export const mergeEmails = (existing: string[], incoming: string[]) => {
  const seen = new Set(existing.map((email) => email.toLowerCase()));
  const merged = [...existing];
  incoming.forEach((email) => {
    const key = email.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(email);
    }
  });
  return merged;
};

export const postBulkEmailRecipients = async ({
  siteId,
  cameraIds,
  mode,
  emails,
  dryRun,
}: BulkEmailRequest): Promise<BulkEmailResponse> => {
  const body: Record<string, unknown> = {
    site_id: siteId,
    mode,
    emails,
    dry_run: dryRun,
  };
  if (cameraIds) body.camera_ids = cameraIds;
  const response = await createApi("").post(
    "cameras/bulk_ses_recipients/",
    body
  );
  return response.data as BulkEmailResponse;
};

export const NO_PERMISSION_MESSAGE =
  "You don't have permission to edit cameras on this site.";
export const SITE_NOT_FOUND_MESSAGE = "This site couldn't be found.";

/** Plain-language message for a failed preview/apply call. */
export const bulkEmailErrorMessage = (error: unknown): string => {
  const axiosError = error as AxiosError<{ code?: string; invalid?: unknown }>;
  const status = axiosError?.response?.status;
  const data = axiosError?.response?.data;
  if (status === 403) return NO_PERMISSION_MESSAGE;
  if (status === 404) return SITE_NOT_FOUND_MESSAGE;
  if (status === 400 && data && typeof data === "object") {
    switch (data.code) {
      case "invalid_emails": {
        const invalid = Array.isArray(data.invalid) ? data.invalid : [];
        return invalid.length
          ? `These emails aren't valid: ${invalid.join(", ")}`
          : "One or more emails aren't valid.";
      }
      case "no_emails":
        return "Enter at least one email address.";
      case "unknown_cameras":
        return "Some of the selected cameras are no longer on this site. Reload the page and try again.";
      case "bad_mode":
        return "Choose Add, Remove or Replace and try again.";
      default:
        break;
    }
  }
  return "Something went wrong updating email alert recipients. Please try again.";
};
